import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { form } from '@angular/forms/signals';

import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import { ELineStyle } from '@common/enums';

import type { AdvancedBorder } from '@sites/entity-core';

import { ThemeStateService } from '@sites/site-theme/src/state';

import { EBorderSide } from '../sp-advanced-border-settings/type/advanced-border-settings.type';
import type { ColorPickerEventPayload } from '../sp-color-picker/src/types/color-picker.type';

import { SpInputNumComponent } from '../sp-form-controls/sp-input-num/sp-input-num.component';
import { SpColorPickerComponent } from '../sp-color-picker/src/sp-color-picker.component';

const DEFAULT_SETTINGS: AdvancedBorder = {
    isActive: false,
    isIndependentBorders: false,
    borderTopWidth: 0,
    borderTopColor: '#000000',
    borderTopStyle: ELineStyle.SOLID,
    borderRightWidth: 0,
    borderRightColor: '#000000',
    borderRightStyle: ELineStyle.SOLID,
    borderBottomWidth: 0,
    borderBottomColor: '#000000',
    borderBottomStyle: ELineStyle.SOLID,
    borderLeftWidth: 0,
    borderLeftColor: '#000000',
    borderLeftStyle: ELineStyle.SOLID,
};

/**
 * Signal-form editor for the per-side border settings — the signal-form
 * analogue of `sp-advanced-border-settings`, retyped to the canonical
 * `AdvancedBorder` value from `@sites/entity-core` (i.e. exactly what a widget
 * signal form's `advancedBorderSettings` branch holds, with `border*Style`
 * kept as plain strings). Same value-in / event-out contract as
 * `sp-animation-settings-signal-legacy`: the parent binds a snapshot via the
 * `borderSettings` input and persists each `valueChangeEvent` through
 * `form.setAdvancedBorderSettings(value)`.
 */
@Component({
    selector: 'sp-advanced-border-settings-signal-legacy',
    templateUrl: './sp-advanced-border-settings-signal-legacy.component.html',
    styleUrls: ['./sp-advanced-border-settings-signal-legacy.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TooltipModule, SpInputNumComponent, SpColorPickerComponent, TranslateModule],
})
export class SpAdvancedBorderSettingsSignalLegacyComponent {
    private readonly themeState = inject(ThemeStateService);

    public readonly borderSettings = input<AdvancedBorder | null>(null);
    public readonly valueChangeEvent = output<AdvancedBorder>();

    public readonly EBorderSide = EBorderSide;
    public readonly lineStyle = ELineStyle;

    public readonly model = signal<AdvancedBorder>(DEFAULT_SETTINGS);
    public readonly borderForm = form(this.model);

    public readonly isActive = computed(() => this.borderForm.isActive().value());
    public readonly isIndependentBorders = computed(() => this.borderForm.isIndependentBorders().value());

    public readonly selectedThemeColors = this.themeState.selectedColors;

    constructor() {
        effect(() => {
            const incoming = this.borderSettings();
            if (!incoming) return;

            this.model.set({ ...DEFAULT_SETTINGS, ...incoming });
        });
    }

    public toggleIsActive(isActive: boolean): void {
        this.model.update((m) => ({ ...m, isActive }));
        this.emitChanges();
    }

    public toggleIndependentBorders(): void {
        this.model.update((m) => {
            const next = !m.isIndependentBorders;

            if (next) {
                return { ...m, isIndependentBorders: next };
            }

            return {
                ...m,
                isIndependentBorders: next,
                borderRightWidth: m.borderTopWidth,
                borderBottomWidth: m.borderTopWidth,
                borderLeftWidth: m.borderTopWidth,
                borderRightColor: m.borderTopColor,
                borderBottomColor: m.borderTopColor,
                borderLeftColor: m.borderTopColor,
                borderRightStyle: m.borderTopStyle,
                borderBottomStyle: m.borderTopStyle,
                borderLeftStyle: m.borderTopStyle,
            };
        });
        this.emitChanges();
    }

    public setBorderWidth(value: number, side: EBorderSide): void {
        this.model.update((m) => {
            switch (side) {
                case EBorderSide.top:
                    return { ...m, borderTopWidth: value };
                case EBorderSide.right:
                    return { ...m, borderRightWidth: value };
                case EBorderSide.bottom:
                    return { ...m, borderBottomWidth: value };
                case EBorderSide.left:
                    return { ...m, borderLeftWidth: value };
                default:
                    return {
                        ...m,
                        borderTopWidth: value,
                        borderRightWidth: value,
                        borderBottomWidth: value,
                        borderLeftWidth: value,
                    };
            }
        });
        this.emitChanges();
    }

    public setBorderColor({ value }: ColorPickerEventPayload, side: EBorderSide): void {
        this.model.update((m) => {
            switch (side) {
                case EBorderSide.top:
                    return { ...m, borderTopColor: value };
                case EBorderSide.right:
                    return { ...m, borderRightColor: value };
                case EBorderSide.bottom:
                    return { ...m, borderBottomColor: value };
                case EBorderSide.left:
                    return { ...m, borderLeftColor: value };
                default:
                    return {
                        ...m,
                        borderTopColor: value,
                        borderRightColor: value,
                        borderBottomColor: value,
                        borderLeftColor: value,
                    };
            }
        });
        this.emitChanges();
    }

    public setBorderStyle(value: string, side: EBorderSide): void {
        this.model.update((m) => {
            switch (side) {
                case EBorderSide.top:
                    return { ...m, borderTopStyle: value };
                case EBorderSide.right:
                    return { ...m, borderRightStyle: value };
                case EBorderSide.bottom:
                    return { ...m, borderBottomStyle: value };
                case EBorderSide.left:
                    return { ...m, borderLeftStyle: value };
                default:
                    return {
                        ...m,
                        borderTopStyle: value,
                        borderRightStyle: value,
                        borderBottomStyle: value,
                        borderLeftStyle: value,
                    };
            }
        });
        this.emitChanges();
    }

    private emitChanges(): void {
        this.valueChangeEvent.emit(this.model());
    }
}
